import React, { useState, useEffect } from 'react';
import { payoutsAPI } from '../services/api';
import { Wallet, Send, RefreshCw, CheckCircle, Clock, XCircle, Plus } from 'lucide-react';

const PayoutsPage = () => {
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ amount: '', currency: 'KZT' });
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [balanceRes, payoutsRes] = await Promise.all([
        payoutsAPI.balance(),
        payoutsAPI.list({ page: 1, page_size: 50 })
      ]);
      setBalance(balanceRes.data);
      setPayouts(payoutsRes.data.payouts || []);
    } catch (err) {
      console.error('Payouts error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async () => {
    if (!requestForm.amount) return;
    setRequesting(true);
    try {
      await payoutsAPI.request({ amount: parseFloat(requestForm.amount), currency: requestForm.currency });
      setShowRequestModal(false);
      setRequestForm({ amount: '', currency: 'KZT' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка запроса');
    } finally {
      setRequesting(false);
    }
  };

  const formatAmount = (amount, currency = 'KZT') => {
    if (currency === 'KZT') return `${(amount || 0).toLocaleString('ru-RU')} ₸`;
    return `${((amount || 0) / 100).toFixed(2)} ${currency}`;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, color: 'bg-amber-500/20 text-amber-400', label: 'В обработке' },
      approved: { icon: CheckCircle, color: 'bg-emerald-500/20 text-emerald-400', label: 'Выполнено' },
      rejected: { icon: XCircle, color: 'bg-red-500/20 text-red-400', label: 'Отклонено' }
    };
    const c = config[status] || config.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${c.color}`}><c.icon className="w-3 h-3" />{c.label}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-white">Выплаты</h1>
        <button onClick={fetchData} className="btn-secondary"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/20 rounded-lg"><Wallet className="w-6 h-6 text-emerald-400" /></div>
            <div>
              <p className="text-slate-400 text-sm">Доступно к выплате</p>
              <p className="text-2xl font-bold text-white">{formatAmount(balance?.available)}</p>
            </div>
          </div>
          <button onClick={() => setShowRequestModal(true)} disabled={!balance?.available} className="btn-primary w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Запросить выплату
          </button>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/20 rounded-lg"><Clock className="w-6 h-6 text-amber-400" /></div>
            <div>
              <p className="text-slate-400 text-sm">В обработке</p>
              <p className="text-2xl font-bold text-white">{formatAmount(balance?.pending)}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg"><Send className="w-6 h-6 text-blue-400" /></div>
            <div>
              <p className="text-slate-400 text-sm">Выплачено за месяц</p>
              <p className="text-2xl font-bold text-white">{formatAmount(balance?.paid_month)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payouts History */}
      <div className="card">
        <div className="p-6 border-b border-slate-700"><h3 className="text-lg font-semibold text-white">История выплат</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Сумма</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Статус</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Реквизиты</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Дата запроса</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Дата выплаты</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="p-4 text-white font-mono text-sm">#{p.id}</td>
                  <td className="p-4 text-white font-medium">{formatAmount(p.amount, p.currency)}</td>
                  <td className="p-4">{getStatusBadge(p.status)}</td>
                  <td className="p-4 text-slate-300 text-sm">{p.bank_details || '—'}</td>
                  <td className="p-4 text-slate-400 text-sm">{new Date(p.created_at).toLocaleString('ru-RU')}</td>
                  <td className="p-4 text-slate-400 text-sm">{p.paid_at ? new Date(p.paid_at).toLocaleString('ru-RU') : '—'}</td>
                </tr>
              ))}
              {payouts.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Выплат пока нет</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Запрос на выплату</h3>
            <p className="text-slate-400 text-sm mb-4">Доступно: {formatAmount(balance?.available)}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Сумма</label>
                <input type="number" value={requestForm.amount} onChange={e => setRequestForm(f => ({ ...f, amount: e.target.value }))} max={balance?.available} className="input w-full" placeholder="0" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Валюта</label>
                <select value={requestForm.currency} onChange={e => setRequestForm(f => ({ ...f, currency: e.target.value }))} className="input w-full">
                  <option value="KZT">KZT (тенге)</option>
                  <option value="USD">USD</option>
                  <option value="RUB">RUB</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setShowRequestModal(false)} className="btn-secondary flex-1">Отмена</button>
              <button onClick={handleRequest} disabled={requesting || !requestForm.amount} className="btn-primary flex-1 disabled:opacity-50">
                {requesting ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsPage;
